'use client'

import React, {useState} from "react";
import {APIProvider, Map} from "@vis.gl/react-google-maps";
import {useLoadScript} from "@react-google-maps/api";
import {Places} from "@/types/Places";
import {GpxFile} from "@/types/Paths";
import useMapBounds from "@/utils/hooks/useMapBounds";

type LatLngLiteral = {lat: number; lng: number};

export const defaultMapContainerStyle = {
  width: '100%',
  height: '80vh',
  borderRadius: '15px 0px 0px 15px',
};

type MapProps = {
  lat?: string,
  lng?: string,
  zoom?: number,
  qrPlace?: { latitude: number, longitude: number },
  places?: Places
  className?: string,
  gpxs?: GpxFile[],
  style?: React.CSSProperties,
  children?: React.ReactNode;
}

type MapBodyProps = {
  zoom?: number;
  qrPlace?: { latitude: number, longitude: number };
  lat?: string;
  lng?: string;
  bounds: google.maps.LatLngBounds | undefined;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
};

const computeInitialCenter = (p: {
  qrPlace?: { latitude: number, longitude: number },
  lat?: string,
  lng?: string,
  bounds?: google.maps.LatLngBounds,
}): LatLngLiteral | null => {
  if (p.qrPlace) {
    return {lat: Number(p.qrPlace.latitude), lng: Number(p.qrPlace.longitude)};
  }
  if (p.lat && p.lng && p.lat !== "" && p.lng !== "") {
    return {lat: Number(p.lat), lng: Number(p.lng)};
  }
  if (p.bounds) {
    const c = p.bounds.getCenter();
    return {lat: c.lat(), lng: c.lng()};
  }
  return null;
};

// MapBody monta solo quando isLoaded e' true e almeno una fonte di
// center e' disponibile. Da qui in poi i props non sono piu' osservati
// reattivamente: centro e zoom diventano `defaultCenter`/`defaultZoom`
// (uncontrolled). Cosi' il pan/zoom dell'utente viene preservato e
// nessun setState in effect e' necessario.
const MapBody = ({zoom, qrPlace, lat, lng, bounds, className, style, children}: MapBodyProps) => {
  const [initialCenter] = useState<LatLngLiteral | null>(() =>
    computeInitialCenter({qrPlace, lat, lng, bounds}),
  );
  const [initialZoom] = useState<number>(() => zoom ?? 18);

  if (!initialCenter) return null;

  return (
    <APIProvider apiKey={String(process.env.NEXT_PUBLIC_GOOGLE_MAP_API)}>
      <Map
        mapId={String(process.env.NEXT_PUBLIC_GOOGLE_MAP_ID)}
        className={className}
        defaultCenter={initialCenter}
        defaultZoom={initialZoom}
        style={style}
      >
        {children}
      </Map>
    </APIProvider>
  );
};

const MapComponent = (props: MapProps) => {
  const {zoom, children, className, style, gpxs, qrPlace, lng, lat} = props;
  const bounds = useMapBounds(gpxs);

  const {isLoaded, loadError} = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAP_API || "",
  });

  const gpxsEmpty = gpxs?.filter((itm) => itm.url && itm.url !== "")?.length === 0;

  if (gpxsEmpty && !qrPlace) return null;
  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading maps...</div>;

  // Aspetta che almeno una sorgente di center sia pronta prima di
  // montare MapBody (lazy state init non puo' tornare indietro).
  const hasCenterSource = !!qrPlace || (!!lat && !!lng && lat !== "" && lng !== "") || !!bounds;
  if (!hasCenterSource) return <div>Loading maps...</div>;

  return (
    <MapBody
      zoom={zoom}
      qrPlace={qrPlace}
      lat={lat}
      lng={lng}
      bounds={bounds}
      className={className}
      style={style}
    >
      {children}
    </MapBody>
  );
};

export default MapComponent;
